import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Ticket,
  CreditCard,
  ChevronRight,
  Check,
  AlertCircle,
  Minus,
  Plus,
  Copy,
  ExternalLink,
  ArrowLeft,
  ChevronLeft,
  Play,
  Image as ImageIcon,
  Share2,
  Facebook,
  Twitter,
  MessageCircle,
  Link2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function EventPublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  const [buyerForm, setBuyerForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
  });

  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  // Helper to extract video embed URL
  const getVideoEmbedUrl = (video) => {
    const url = video.url || '';
    if (video.type === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
      // Extract YouTube video ID
      let videoId = '';
      if (url.includes('youtube.com/watch')) {
        videoId = new URL(url).searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1]?.split('?')[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (video.type === 'vimeo' || url.includes('vimeo.com')) {
      // Extract Vimeo video ID
      const vimeoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : null;
    }
    return null;
  };

  // Get all media (main image + gallery)
  const getAllImages = () => {
    const images = [];
    if (event?.image_url) images.push(event.image_url);
    if (event?.gallery_images?.length > 0) {
      images.push(...event.gallery_images.filter(url => url));
    }
    return images;
  };

  // Social sharing functions
  const getShareUrl = () => window.location.href;
  
  const getShareText = () => {
    if (!event) return '';
    return `🎉 ${event.name} - ${format(new Date(event.date), 'PPP', { locale: es })} a las ${event.start_time}`;
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`;
    window.open(url, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    toast.success('Link copiado al portapapeles');
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.name,
          text: getShareText(),
          url: getShareUrl(),
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  // Update meta tags for social sharing
  useEffect(() => {
    if (event) {
      document.title = `${event.name} | OkReserva`;
      
      // Update or create meta tags
      const updateMeta = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                   document.querySelector(`meta[name="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(property.startsWith('og:') ? 'property' : 'name', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMeta('og:title', event.name);
      updateMeta('og:description', event.description?.substring(0, 200) || `Evento el ${event.date}`);
      updateMeta('og:image', event.image_url || '');
      updateMeta('og:url', window.location.href);
      updateMeta('og:type', 'event');
      updateMeta('twitter:card', 'summary_large_image');
      updateMeta('twitter:title', event.name);
      updateMeta('twitter:description', event.description?.substring(0, 200) || '');
      updateMeta('twitter:image', event.image_url || '');
    }
  }, [event]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const res = await eventsAPI.getPublicEvent(slug);
      setEvent(res.data);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Evento no encontrado o no disponible');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketQuantity = (ticketType, delta) => {
    setSelectedTickets(prev => {
      const current = prev[ticketType] || 0;
      const newQuantity = Math.max(0, Math.min(10, current + delta));
      if (newQuantity === 0) {
        const { [ticketType]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketType]: newQuantity };
    });
  };

  const getTotalAmount = () => {
    if (!event) return 0;
    return Object.entries(selectedTickets).reduce((total, [type, qty]) => {
      const ticketInfo = event.ticket_types?.find(t => t.type === type);
      return total + (ticketInfo?.price || 0) * qty;
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

  const handlePurchase = async () => {
    if (!buyerForm.buyer_name || !buyerForm.buyer_email) {
      toast.error('Por favor completa tu nombre y email');
      return;
    }

    setProcessing(true);
    try {
      // Purchase each ticket type
      const results = [];
      for (const [ticketType, quantity] of Object.entries(selectedTickets)) {
        if (quantity > 0) {
          const res = await eventsAPI.publicPurchase(event.id, {
            event_id: event.id,
            ...buyerForm,
            ticket_type: ticketType,
            quantity
          });
          results.push(res.data);
        }
      }
      
      setPurchaseResult(results);
      setPurchaseComplete(true);
      toast.success('¡Compra exitosa!');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array format) or string errors
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Error al procesar la compra');
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const copyQRCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20" />
          <div className="h-4 w-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Evento no disponible</h1>
            <p className="text-muted-foreground mb-6">{error || 'El evento que buscas no existe o ya no está disponible.'}</p>
            <Button onClick={() => navigate('/')} className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (purchaseComplete && purchaseResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted py-12 px-4" data-testid="purchase-success">
        <div className="max-w-lg mx-auto">
          <Card className="border-0 shadow-xl overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-8 text-white text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-10 w-10" />
              </div>
              <h1 className="text-2xl font-bold mb-2">¡Compra Exitosa!</h1>
              <p className="text-white/80">Tus tickets han sido enviados a tu email</p>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Event Summary */}
              <div className="bg-muted rounded-xl p-4">
                <h3 className="font-semibold text-lg mb-2">{event.name}</h3>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(event.date), 'PPPP', { locale: es })}
                  </p>
                  <p className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {event.start_time} hrs
                  </p>
                </div>
              </div>

              {/* QR Codes */}
              <div className="space-y-3">
                <h4 className="font-semibold">Tus códigos de entrada</h4>
                {purchaseResult.map((result, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-xl"
                  >
                    <div>
                      <p className="font-mono font-bold text-primary">{result.qr_code}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.ticket_type} · {result.quantity} entrada(s)
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyQRCode(result.qr_code)}
                      className="rounded-lg"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <span className="font-medium">Total pagado</span>
                <span className="text-2xl font-bold text-primary">
                  ${purchaseResult.reduce((sum, r) => sum + (r.total || 0), 0).toLocaleString()}
                </span>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Presenta estos códigos QR en la entrada del evento
              </p>

              <Button 
                onClick={() => window.print()} 
                variant="outline" 
                className="w-full rounded-xl"
              >
                Imprimir tickets
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted" data-testid="event-public-page">
      {/* Hero Section with Gallery */}
      <div className="relative">
        {getAllImages().length > 0 ? (
          <div className="h-64 md:h-80 overflow-hidden relative">
            <img 
              src={getAllImages()[galleryIndex] || event.image_url} 
              alt={event.name}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setShowGallery(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
            
            {/* Gallery Navigation */}
            {getAllImages().length > 1 && (
              <>
                <button 
                  onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => i > 0 ? i - 1 : getAllImages().length - 1); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => i < getAllImages().length - 1 ? i + 1 : 0); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
                  {getAllImages().map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setGalleryIndex(idx); }}
                      className={cn(
                        "w-2 h-2 rounded-full transition-colors",
                        idx === galleryIndex ? "bg-white" : "bg-white/50"
                      )}
                    />
                  ))}
                </div>
                <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  {galleryIndex + 1}/{getAllImages().length}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="h-40 bg-gradient-to-r from-primary/20 to-primary/10" />
        )}
        
        {/* Event Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-4xl mx-auto flex items-end justify-between">
            <div>
              <Badge className="mb-3 bg-primary/90">{event.restaurant_name}</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{event.name}</h1>
            </div>
            
            {/* Share Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="rounded-xl bg-white/90 hover:bg-white text-black"
                  data-testid="share-event-btn"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartir
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={shareOnFacebook}>
                  <Facebook className="h-4 w-4 mr-2 text-blue-600" />
                  Facebook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareOnTwitter}>
                  <Twitter className="h-4 w-4 mr-2 text-sky-500" />
                  Twitter / X
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareOnWhatsApp}>
                  <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
                  WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={copyLink}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Copiar enlace
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Event Details Card */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Fecha</p>
                      <p className="font-semibold">
                        {format(new Date(event.date), 'PPP', { locale: es })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                    <Clock className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Hora</p>
                      <p className="font-semibold">{event.start_time} - {event.end_time}</p>
                    </div>
                  </div>
                </div>

                {event.description && (
                  <div>
                    <h3 className="font-semibold mb-2">Acerca del evento</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {event.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Video Section */}
            {event.video_urls?.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-5 w-5 text-primary" />
                    Videos del evento
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {event.video_urls.map((video, idx) => {
                    const embedUrl = getVideoEmbedUrl(video);
                    if (!embedUrl) return null;
                    return (
                      <div key={idx}>
                        {video.title && (
                          <h4 className="font-medium mb-2">{video.title}</h4>
                        )}
                        <div className="aspect-video rounded-xl overflow-hidden bg-muted">
                          <iframe
                            src={embedUrl}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={video.title || `Video ${idx + 1}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Gallery Thumbnails */}
            {getAllImages().length > 1 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    Galería de fotos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-2">
                    {getAllImages().map((url, idx) => (
                      <button
                        key={idx}
                        onClick={() => { setGalleryIndex(idx); setShowGallery(true); }}
                        className={cn(
                          "aspect-square rounded-lg overflow-hidden border-2 transition-colors",
                          idx === galleryIndex ? "border-primary" : "border-transparent"
                        )}
                      >
                        <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tickets Selection */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  Selecciona tus tickets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {event.ticket_types?.map((ticket) => {
                  const available = ticket.available ?? (ticket.quantity - (ticket.sold || 0));
                  const isSoldOut = available <= 0;
                  const selectedQty = selectedTickets[ticket.type] || 0;

                  return (
                    <div 
                      key={ticket.type}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-xl border-2 transition-all",
                        selectedQty > 0 
                          ? "border-primary bg-primary/5" 
                          : "border-transparent bg-muted",
                        isSoldOut && "opacity-50"
                      )}
                      data-testid={`ticket-type-${ticket.type}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{ticket.name}</h4>
                          {isSoldOut && (
                            <Badge variant="destructive" className="text-xs">Agotado</Badge>
                          )}
                        </div>
                        <p className="text-2xl font-bold text-primary mt-1">
                          ${ticket.price?.toLocaleString()}
                        </p>
                        {event.show_remaining_tickets && !isSoldOut && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {available} disponibles
                          </p>
                        )}
                      </div>

                      {!isSoldOut && (
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => updateTicketQuantity(ticket.type, -1)}
                            disabled={selectedQty === 0}
                            className="rounded-full h-10 w-10"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-bold text-lg">
                            {selectedQty}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => updateTicketQuantity(ticket.type, 1)}
                            disabled={selectedQty >= Math.min(10, available)}
                            className="rounded-full h-10 w-10"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Order Summary */}
          <div className="md:col-span-1">
            <Card className="border-0 shadow-lg sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Tu orden</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {getTotalTickets() === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Selecciona tus tickets para continuar
                  </p>
                ) : (
                  <>
                    {/* Selected tickets summary */}
                    <div className="space-y-2">
                      {Object.entries(selectedTickets).map(([type, qty]) => {
                        const ticketInfo = event.ticket_types?.find(t => t.type === type);
                        return (
                          <div key={type} className="flex justify-between text-sm">
                            <span>{qty}x {ticketInfo?.name}</span>
                            <span className="font-medium">
                              ${((ticketInfo?.price || 0) * qty).toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Total</span>
                        <span className="text-2xl font-bold text-primary">
                          ${getTotalAmount().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <Button 
                      onClick={() => setShowCheckout(true)}
                      className="w-full rounded-xl"
                      size="lg"
                      data-testid="checkout-btn"
                    >
                      Continuar <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Finalizar compra
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Order Summary */}
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <h4 className="font-semibold">{event.name}</h4>
              <p className="text-sm text-muted-foreground">
                {format(new Date(event.date), 'PPP', { locale: es })} · {event.start_time}
              </p>
              <div className="border-t pt-2 mt-2">
                {Object.entries(selectedTickets).map(([type, qty]) => {
                  const ticketInfo = event.ticket_types?.find(t => t.type === type);
                  return (
                    <div key={type} className="flex justify-between text-sm">
                      <span>{qty}x {ticketInfo?.name}</span>
                      <span>${((ticketInfo?.price || 0) * qty).toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total</span>
                <span className="text-primary">${getTotalAmount().toLocaleString()}</span>
              </div>
            </div>

            {/* Buyer Info Form */}
            <div className="space-y-3">
              <div>
                <Label>Nombre completo *</Label>
                <Input
                  value={buyerForm.buyer_name}
                  onChange={(e) => setBuyerForm(prev => ({ ...prev, buyer_name: e.target.value }))}
                  placeholder="Tu nombre"
                  className="rounded-xl"
                  data-testid="checkout-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={buyerForm.buyer_email}
                  onChange={(e) => setBuyerForm(prev => ({ ...prev, buyer_email: e.target.value }))}
                  placeholder="tu@email.com"
                  className="rounded-xl"
                  data-testid="checkout-email-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Aquí recibirás tus tickets
                </p>
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={buyerForm.buyer_phone}
                  onChange={(e) => setBuyerForm(prev => ({ ...prev, buyer_phone: e.target.value }))}
                  placeholder="+52 55 1234 5678"
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCheckout(false)}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handlePurchase}
              disabled={processing || !buyerForm.buyer_name || !buyerForm.buyer_email}
              className="rounded-xl"
              data-testid="confirm-purchase-btn"
            >
              {processing ? (
                <>Procesando...</>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pagar ${getTotalAmount().toLocaleString()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Gallery Modal */}
      {showGallery && getAllImages().length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setShowGallery(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setShowGallery(false)}
          >
            <span className="text-3xl">×</span>
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => i > 0 ? i - 1 : getAllImages().length - 1); }}
            className="absolute left-4 text-white/80 hover:text-white p-4"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          
          <img 
            src={getAllImages()[galleryIndex]} 
            alt={`Foto ${galleryIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            onClick={(e) => { e.stopPropagation(); setGalleryIndex(i => i < getAllImages().length - 1 ? i + 1 : 0); }}
            className="absolute right-4 text-white/80 hover:text-white p-4"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80">
            {galleryIndex + 1} / {getAllImages().length}
          </div>
        </div>
      )}
    </div>
  );
}
